"use client";

import { ChevronLeft } from "lucide-react";
import { useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { useCalendarContext } from "../calendar-context";
import { getEventColorClasses } from "../event-colors";
import { POPOVER_CLASSES } from "../styles";
import { type CalendarEvent } from "../types";
import { formatDayLabel, formatTime } from "../utils";

import { EventDetail } from "./event-popover";

// =============================================================================

type MoreEventsButtonProps = {
  day: Date;
  /** Events of the cell that did not fit */
  events: CalendarEvent[];
};

/**
 * "+N more" button of a month cell: a popover lists the hidden events, and
 * opening one shows its details in place.
 */
export function MoreEventsButton({ day, events }: MoreEventsButtonProps) {
  const { locale, labels, onEventClick } = useCalendarContext();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<CalendarEvent | null>(null);

  const label = labels.more(events.length);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSelected(null);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={label}
          className="pointer-events-auto h-5 shrink-0 truncate rounded-md px-1 text-left text-xs leading-5 font-medium text-muted-foreground transition-colors outline-none hover:bg-surface-2 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-inset"
        >
          {/* Narrow cells: the count alone */}
          <span aria-hidden className="sm:hidden">
            +{events.length}
          </span>
          <span aria-hidden className="max-sm:hidden">
            {label}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className={cn(POPOVER_CLASSES, "p-1")}
        onClick={(event) => event.stopPropagation()}
      >
        {selected ? (
          <div>
            <button
              type="button"
              aria-label={labels.previous}
              // The clicked row unmounted: keep the focus inside the popover
              autoFocus
              onClick={() => setSelected(null)}
              className="m-1 mb-0 grid size-7 place-items-center rounded-md text-muted-foreground transition-colors outline-none hover:bg-signal-tint hover:text-signal focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <ChevronLeft className="size-4" />
            </button>
            <EventDetail event={selected} />
          </div>
        ) : (
          <>
            <p className="px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">
              {formatDayLabel(day, locale)}
            </p>
            <ul>
              {events.map((event) => (
                <li key={event.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(event);
                      onEventClick?.(event);
                    }}
                    className="flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors outline-none hover:bg-surface-2 focus-visible:ring-2 focus-visible:ring-ring/40"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        getEventColorClasses(event.color).dot
                      )}
                    />
                    {!event.allDay && (
                      <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                        {formatTime(event.start, locale)}
                      </span>
                    )}
                    <span className="truncate font-medium text-foreground">
                      {event.title}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
